import {Component, Input, Output, EventEmitter, OnChanges} from '@angular/core';
import {OptionService} from "../option/option.service";
import {TranslateService} from "@ngx-translate/core";
import {Donation} from "../donation/donation";
import {StatusComponent} from "../output/output.status.component";
import {ContactComponent} from "../contact/contact.component";
import {DecimalPipe} from "@angular/common";

@Component({
    selector: 'step-navigation',
    templateUrl: './step.navigation.html',
    providers: [DecimalPipe]
})
export class StepNavigationComponent implements OnChanges {
    /**
     * The current step of the app
     */
    @Input() step: string;

    /**
     * The current donation in the app
     */
    @Input() donation: Donation;

    /**
     * Output parameter for changing the step
     * @type {EventEmitter}
     */
    @Output() stepChange: EventEmitter<any> = new EventEmitter();

    /**
     * The status of the donation
     */
    @Input() donationStatus: string;

    /**
     * The size of the users viewport
     */
    @Input() size: string;

    /**
     * Output parameter for changes on the donationStatus
     * @type {EventEmitter}
     */
    @Output() donationStatusChange: EventEmitter<any> = new EventEmitter();

    /**
     * force the header to be shown
     *
     * @type {boolean}
     */
    @Input() forceShow: boolean = null;

    /**
     * force a specific text for the navigation
     */
    @Input() forceText: string = null;

    /**
     * Holds the type of the project
     */
    @Input() projectType: string = null;

    /**
     * indicates if we are in the digital download process
     */
    @Input() isDigitalDownloadProcess: boolean = false;

    /**
     * Text to be shown in the header
     * @type {any}
     */
    text: string = null;

    /**
     * The current type of the app
     */
    type: string;

    /**
     * List of steps where the navigation should be visible
     * @type {[string,string]}
     */
    visibleOnSteps: any = ["payment", "payment-select", "iframe", "banktransfer", "dataprotection", "impress", "contact", "contact-mandatory", "contact-mandatory-finished", "detail", "cart", "bonus-details", "download"];
    visibleOnGiftShopSteps: any = ["iframe", "banktransfer", "dataprotection", "impress", "contact", "contact-mandatory-finished"];

    /**
     * List of types where the navigation should be visible
     * @type {[string,string]}
     */
    visbleOnFormSteps: any = ["dataprotection", "impress", "contact", "iframe", "banktransfer", "bonus-details"];

    /**
     * saves the steps before the current step
     * @type {any}
     */
    previousStep: string = null;

    /**
     * Holds if the contact data is requested mandatory
     * @type {boolean}
     */
    contactMandatory: boolean = false;

    /**
     * The constructor
     * @param {OptionService} optionService
     * @param {TranslateService} translationService
     * @param {DecimalPipe} decimalPipe
     */
    constructor(private optionService: OptionService, private translationService: TranslateService, private decimalPipe: DecimalPipe) {}

    /**
     * Initialize the slider
     */
    public ngOnInit(): any {
        this.type = this.optionService.getType();

        // check if the contact data should be requested manually
        this.contactMandatory = ContactComponent.isContactMandatory();
    }

    /**
     * method is called every time some model changes (for us only the step is interesting)
     * @param changes
     */
    public ngOnChanges(changes: any) {
        // don't save some steps in the previous steps
        if(changes.step && changes.step.previousValue && changes.step.previousValue != "dataprotection" && changes.step.previousValue != "impress") {
            this.previousStep = changes.step.previousValue;
        }
    }

    /**
     * Returns the text that is shown in the header
     * @returns {string}
     */
    getText(): string {
        if(this.forceText !== null) {
            return this.forceText;
        }

        switch(this.step) {
            case "banktransfer":
            case "dataprotection":
            case "impress":
            case "contact":
            case "bonus-details":
                this.text = this.translationService.instant("stepnavigation.back");
                break;
            default:
                // format the number in the header
                if(this.projectType === 'membership') {
                    this.text = '<br>' + this.donationTarget();
                }
                else if(this.projectType === 'shop') {
                    if(this.step === 'cart' || this.step === 'detail') {
                        this.text = this.translationService.instant("stepnavigation.back.shop");
                    }
                    else {
                        this.text = this.translationService.instant("stepnavigation.back");
                    }
                }
                else {
                    this.text = this.getFormattedAmount(this.donation.amount) + ' ' + this.donation.currencySymbol + '<br>' + this.donationTarget();
                }
                break;
        }
        return this.text;
    }

    /**
     * Returns the formatted amount
     * @param amount
     */
    getFormattedAmount(amount: number): string {
        const hasDecimals = amount % 1 !== 0;
        let amountFormatted = this.decimalPipe.transform(amount, hasDecimals ? '1.2-2' : '1.0-0');
        if(this.donation.extraAmount > 0) {
            amountFormatted += ' + ' + this.decimalPipe.transform(this.donation.extraAmount);
        }
        return amountFormatted;
    }

    /**
     * Returns the textkey for the recurring ryth in case this should be a recurring donation
     * @returns {string}
     */
    getTextPrefixTextkey(): string {
        if(this.forceText !== null) {
            return "";
        }

        switch(this.step) {
            case "banktransfer":
            case "dataprotection":
            case "impress":
            case "contact":
            case "bonus-details":
                return "";
            default:
                let label = "";
                if(this.donation.rythm != "one_time") {
                    label =  "rythm.recurring." + this.donation.recurringRythm + ".label";
                    if(this.projectType === 'membership') {
                        label += '.membership';
                    }

                }
                return label;
        }
    }

    /**
     * Returns if the component should be visible or not
     * @returns {boolean}
     */
    showNavigation(): boolean {
        if(this.forceShow !== null) {
            return this.forceShow;
        }

        switch(this.type) {
            case "widget":
            case "page":
            case "shop":
                return this.visibleOnSteps.indexOf(this.step) != -1 || (this.step === "thankyou" && this.isDigitalDownloadProcess);
            case "giftshop":
                return this.visibleOnGiftShopSteps.indexOf(this.step) != -1;
            case "form":
                return this.visbleOnFormSteps.indexOf(this.step) != -1;
        }
    }

    /**
     * Return the donation target shat should be visible in the top navigation
     * @returns {any}
     */
    donationTarget(): string {
        let donationTargets = this.optionService.getDonationTargets();
        if(donationTargets.length == 0) {
            // Show name of the organisation
            return this.optionService.getOrganisationData("name");
        }
        else {
            // show the selected target
            return this.donation.target;
        }
    }

    /**
     * allow leyboard usage of the component
     * @param event
     */
    onKeyDownGoBack(event: Event) {
      event.preventDefault();
      this.stepBack();
    }

    /**
     * Steps one step back
     */
    stepBack(): void {
        const defaultBackStep = this.projectType === 'shop' ? 'cart' : 'start';

        switch (this.step) {
            case "payment":
                if ((this.type === "widget" || this.type === "shop" || this.type === "page") && (this.size !== "small" || this.donation.isAmountZero() === true) ) {
                    if (this.contactMandatory === true) {
                        this.step = "contact-mandatory";
                    }
                    else {
                        this.step = this.isDigitalDownloadProcess ? 'download' : defaultBackStep;
                    }
                }
                else {
                    this.step = "payment-select";
                }
                break;
            case "payment-select":
                if(this.contactMandatory == true) {
                    this.step = "contact-mandatory";
                }
                else {
                    this.step = this.isDigitalDownloadProcess ? 'download' : defaultBackStep;
                }
                break;
            case "contact-mandatory":
                this.step = this.isDigitalDownloadProcess ? 'download' : defaultBackStep;
                break;
            case "iframe":
                if(this.donationStatus == StatusComponent.IFRAME) {
                    // reset the status back to OK
                    this.donationStatus = StatusComponent.OK;
                    this.donationStatusChange.emit(this.donationStatus);
                }
                if(this.size == "small" && this.type != "form") {
                    this.step = "payment";
                }
                else {
                    this.step = defaultBackStep;
                }
                break;
            case "banktransfer":
            case "contact":
                this.step = "thankyou";
                break;
            case "detail":
            case "cart":
                this.step = "list";
                break;
            case "thankyou":
                this.step = this.isDigitalDownloadProcess ? 'list' : defaultBackStep;;
                break;
            case "download":
                this.step = (this.previousStep === 'list' || this.previousStep === 'detail') ? this.previousStep : 'list';
                break;          
            default:
                if(this.previousStep) {
                    // one step back
                    this.step = this.previousStep;
                }
                else {
                    // default to the start step
                    this.step = defaultBackStep;
                }
                break;
        }
        this.stepChange.emit(this.step);
    }

    /**
     * Returns a list of placeholders that we need inside the last submit button in the widget
     */
    getPlaceholders(): any {
        return {
            donationAmount: this.getFormattedAmount(this.donation.amount),
            donationAmountx3: this.getFormattedAmount(this.donation.amount * 3),
            donationAmountx6: this.getFormattedAmount(this.donation.amount * 6),
            donationAmountx12: this.getFormattedAmount(this.donation.amount * 12),
            currencySymbol: this.donation.currencySymbol
        };
    }
}
